import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { isCustomer } from '../../../api';
import { cancelHangoutInvite, toApiError } from '../api';
import { useMyHangoutInvites } from '../hooks';
import { HangoutInvite } from '../types';
import { errorTitle, isPast, loginUrlFor } from '../utils';
import { EmptyState, ErrorState, PageTop, SkeletonList } from '../components/common';
import { InviteCard } from '../components/InviteCard';
import { ShareInviteSheet } from '../components/ShareInviteSheet';
import '../hangouts.css';

type Section = 'Upcoming' | 'Created by Me' | 'Joined' | 'Past';
const SECTIONS: Section[] = ['Upcoming', 'Created by Me', 'Joined', 'Past'];

const isOver = (i: HangoutInvite) => ['Cancelled', 'Expired', 'Completed'].includes(i.status) || isPast(i.inviteDateTime);

const inSection = (i: HangoutInvite, s: Section) => {
  switch (s) {
    case 'Upcoming': return !isOver(i);
    case 'Created by Me': return i.isHost && !isOver(i);
    case 'Joined': return !i.isHost && !isOver(i) && (i.myStatus === 'Going' || i.myStatus === 'Interested' || i.myStatus === 'Requested');
    case 'Past': return isOver(i);
  }
};

const EMPTY: Record<Section, { title: string; body: string }> = {
  Upcoming: { title: "You don't have any upcoming hangouts", body: 'Open a venue and tap "Invite to Hangout" to plan a night out.' },
  'Created by Me': { title: "You haven't created any invites", body: 'Pick a venue and invite your friends.' },
  Joined: { title: "You haven't joined any hangouts yet", body: 'When friends share an invite with you, it shows up here.' },
  Past: { title: 'No past hangouts', body: 'Nights out you were part of will appear here.' }
};

// Profile → My Invites (/profile/invites).
const MyInvitesPage: React.FC = () => {
  const location = useLocation();
  const signedIn = isCustomer();
  const { data, error, loading, reload } = useMyHangoutInvites();
  const [section, setSection] = useState<Section>('Upcoming');
  const [sharing, setSharing] = useState<HangoutInvite | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  if (!signedIn) return <Navigate to={loginUrlFor(location.pathname)} replace />;

  const cancel = async (invite: HangoutInvite) => {
    if (!window.confirm(`Cancel your ${invite.venue.name} hangout? Everyone who responded will be notified.`)) return;
    try {
      await cancelHangoutInvite(invite.id);
      setNotice({ ok: true, text: 'Hangout cancelled. Everyone who responded has been notified.' });
      await reload();
    } catch (err) {
      setNotice({ ok: false, text: toApiError(err).message });
    }
  };

  const all = data ?? [];
  const shown = all.filter((i) => inSection(i, section)).sort((a, b) =>
    section === 'Past' ? b.inviteDateTime.localeCompare(a.inviteDateTime) : a.inviteDateTime.localeCompare(b.inviteDateTime));

  return (
    <main className="hg">
      <div className="hg-wrap">
        <PageTop title="My Invites" backTo="/" />
        <div className="hg-tabs" role="tablist">
          {SECTIONS.map((s) => (
            <button key={s} type="button" role="tab" aria-selected={section === s} className={`hg-tab ${section === s ? 'on' : ''}`} onClick={() => setSection(s)}>
              {s} ({all.filter((i) => inSection(i, s)).length})
            </button>
          ))}
        </div>
        {notice && <div className={`hg-notice ${notice.ok ? 'success' : 'error'}`} role="status">{notice.text}</div>}
        {error ? (() => { const e = errorTitle(error); return <ErrorState title={e.title} body={e.body} />; })()
          : loading && !data ? <SkeletonList rows={3} height={130} />
          : shown.length === 0 ? <EmptyState {...EMPTY[section]} action={<Link className="hg-btn hg-gold" to="/explore">Find a venue</Link>} />
          : <div className="hg-list">{shown.map((i) => <InviteCard key={i.id} invite={i} onShare={setSharing} onCancel={cancel} />)}</div>}
      </div>
      {sharing && <ShareInviteSheet invite={sharing} publicToken={sharing.publicToken} refCode={sharing.isHost ? null : sharing.myRefCode} onClose={() => setSharing(null)} />}
    </main>
  );
};

export default MyInvitesPage;
